import moment from 'moment';
import React from 'react';
import Select from '../atoms/Select';

export type DateRangeOption =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisYear'
  | 'lastYear';

export type DateRangeSelectorProps = {
  value: DateRangeOption;
  onChange: (value: DateRangeOption, range: { from: moment.Moment; to: moment.Moment }) => void;
  options?: DateRangeOption[];
  className?: string;
};

const DATE_RANGE_LABELS: Record<DateRangeOption, string> = {
  today: '今日',
  yesterday: '前日',
  thisWeek: '今週',
  lastWeek: '前週',
  thisMonth: '今月',
  lastMonth: '前月',
  thisYear: '今年',
  lastYear: '前年',
};

const DEFAULT_OPTIONS: DateRangeOption[] = ['today', 'yesterday', 'thisMonth', 'lastMonth'];

const EXTENDED_OPTIONS: DateRangeOption[] = [
  'today',
  'yesterday',
  'thisWeek',
  'lastWeek',
  'thisMonth',
  'lastMonth',
  'thisYear',
  'lastYear',
];

export { DEFAULT_OPTIONS, EXTENDED_OPTIONS };

const getDateRanges = () => ({
  today: {
    from: moment().startOf('day'),
    to: moment().endOf('day'),
  },
  yesterday: {
    from: moment().subtract(1, 'day').startOf('day'),
    to: moment().subtract(1, 'day').endOf('day'),
  },
  thisWeek: {
    from: moment().startOf('week'),
    to: moment().endOf('day'),
  },
  lastWeek: {
    from: moment().subtract(1, 'week').startOf('week'),
    to: moment().subtract(1, 'week').endOf('week'),
  },
  thisMonth: {
    from: moment().startOf('month'),
    to: moment().endOf('day'),
  },
  lastMonth: {
    from: moment().subtract(1, 'month').startOf('month'),
    to: moment().subtract(1, 'month').endOf('month'),
  },
  thisYear: {
    from: moment().startOf('year'),
    to: moment().endOf('day'),
  },
  lastYear: {
    from: moment().subtract(1, 'year').startOf('year'),
    to: moment().subtract(1, 'year').endOf('year'),
  },
});

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  value,
  onChange,
  options = DEFAULT_OPTIONS,
  className = '',
}) => {
  const selectOptions = options.map((option) => ({
    label: DATE_RANGE_LABELS[option],
    value: option,
  }));

  const selectedOption = selectOptions.find((option) => option.value === value);

  const handleChange = (selectedValue: { label: string; value: DateRangeOption } | null) => {
    if (selectedValue) {
      const dateRanges = getDateRanges();
      const range = dateRanges[selectedValue.value] || dateRanges.today;
      onChange(selectedValue.value, range);
    }
  };

  return (
    <Select
      className={className}
      options={selectOptions}
      value={selectedOption}
      onChange={handleChange}
      isSearchable={false}
      maxMenuHeight={9999}
    />
  );
};
